<template>
  <div class="member-search mt-5">


    <v-breadcrumbs
        class="level-2"
        :items="[
            {text: $t('expense-refund.expense-refunds'), to: {name: 'organizations.expense-periods'}, exact: true},
            {text: periodName },
        ]"
        divider=">"
    ></v-breadcrumbs>

    <v-row dense>
      <v-col class="text-right">




        <!-- Close only once the period has ENDED: closing an unfinished period would invoice its proformas
             mid-cycle (the July incident). The backend enforces this too; here we hide the button + explain. -->
        <v-btn
            small
            color="primary"
            class="mr-3"
            @click="openClosureModal"
            v-if="status === 'PENDING' && periodEnded && currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')"
            :disabled="closureLoading"
            :loading="closureLoading"
        >
          <v-icon left>mdi-lock</v-icon>
          {{ $t('expense-refund.closure.confirm-title') }}
        </v-btn>

        <v-chip
            v-if="status === 'PENDING' && !periodEnded && currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')"
            small label color="warning" outlined class="mr-3"
        >
          <v-icon left small>mdi-clock-alert-outline</v-icon>
          {{ $t('expense-refund.closure.period-not-ended') }}
        </v-chip>

        <v-btn
            small
            class="mr-3"
            @click="openClosureLog"
            v-if="status === 'DONE' && currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')"
            :loading="closureLogLoading"
        >
          <v-icon left>mdi-clipboard-text-clock-outline</v-icon>
          {{ $t('expense-refund.closure.view-log') }}
        </v-btn>

        <v-btn small @click="openSendMailModal" v-if="currentUser.isZE">
          <v-icon left>mdi-send</v-icon>
          Envoyer par mail
        </v-btn>


        <v-btn small class="ml-3" @click="syncPeriod" v-if="['PENDING', 'IN_PROGRESS'].includes(status)">
          <v-icon left>mdi-sync</v-icon>
          Sync CDR
        </v-btn>


        <v-btn small class="ml-3" @click="exportGlobal" v-if="currentUser.isZE">
          <v-icon left>mdi-file-delimited</v-icon>
          Global
        </v-btn>


        <v-btn small class="ml-3" @click="exportGlobalQonto" v-if="currentUser.isZE">
          <v-icon left>mdi-file-delimited</v-icon>
          Qonto (global)
        </v-btn>


        <v-btn class="mx-3" small @click="exportSheet">
          <v-icon left>mdi-file-excel</v-icon>
          Xlsx
        </v-btn>

        <v-btn small @click="exportPdf">
          <v-icon left>mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>
        <audit-trail-button class="ml-2" entity-type="ExpensePeriod" :entity-id="$route.params.periodId" />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            <h3>{{ $t('expense-refund.expense-period') }}</h3>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                        :value="formatUtcDate(startDate, $t('format_date'))"
                        :label="$t('expense-refund.start-date')"
                        readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                        :value="formatUtcDate(endDate, $t('format_date'))"
                        :label="$t('expense-refund.end-date')"
                        readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                    :value="status ? $t('expense-refund.status.' + status ) : '-'"
                    :label="$t('status')"
                    readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" v-if="closedByUserName || closedAt">
                <v-text-field
                    :value="closureInfo"
                    :label="$t('expense-refund.closure.confirm-title')"
                    readonly
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>


    <v-row>
      <v-col>
        <search-field
            :placeholder="$t('search')"
            :search.sync="search"
            :options.sync="options"
            clearable
        >
        </search-field>
      </v-col>
    </v-row>



    <v-row dense v-if="hasMembersWithoutCharges">
      <v-col class=" d-flex justify-end">
        <v-switch
            v-model="showMembersWithoutCharges"
            :label="$t('expense-refund.show-members-without-recharges')"
            hide-details
        />
      </v-col>
    </v-row>


    <!-- List -->
    <v-row no-gutters class="mt-10">
      <v-col>
        <v-data-table
            class="member-list"
            :headers="headers"
            :items="memberFiltered"
            :loading="loading"
            :options="options"
        >
          <template #item.customer.lastname="{item}">
            <router-link :to="{name: 'organizations.expense-periods.member', params: {periodId: periodId, memberId: item.id}}">
              {{ item.customer?.firstname }} {{ item.customer?.lastname }}
            </router-link>
          </template>

          <template #item.energy="{value}">
            {{ numberFormat(value/ 1000, 3) }} kWh
          </template>

          <template #item.amount="{value}">
            {{ numberFormat(value) }} €
          </template>


        </v-data-table>

      </v-col>
    </v-row>


    <expense-refund-send-mail-modal ref="sendMailModal" :members="members" :period-id="periodId" :period-status="status" />
    <expense-period-closure-modal ref="closureModal" :period-id="periodId" :organization-id="organization ? organization.id : null" :member-count="members.length" :members="members" @closed="onClosureDone" />

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import dayjs from "dayjs";
import ExpenseRefundRepository from "@repository/ExpenseRefundRepository";
import ExpenseRefundSendMailModal from "@modals/ExpenseRefundSendMailModal.vue";
import ExpensePeriodClosureModal from "@modals/ExpensePeriodClosureModal.vue";

export default {
  components: {
    AuditTrailButton,
    ExpenseRefundSendMailModal,
    ExpensePeriodClosureModal,
  },

  data() {
    return {
      search: '',
      startDate: null,
      endDate: null,
      status: null,
      closedByUserName: null,
      closedAt: null,
      loading: false,
      closureLoading: false,
      closureLogLoading: false,
      autoSynced: false,
      showMembersWithoutCharges: false,
      members: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['customer.lastname'],
        sortDesc: [false],
      },
    }
  },

  mounted() {
    this.loadData('init').then(() => {
      this.checkClosureProgress()
    })
  },



  computed: {

    // A period can only be closed once it has actually ended (its end date is in the past). Guards against
    // closing/invoicing a not-yet-finished month — mirror of the backend guard.
    periodEnded() {
      return this.endDate ? dayjs(this.endDate).isBefore(dayjs()) : false
    },

    headers() {
      return [
        {text: this.$t('name'), value: 'customer.lastname'},
        {text: this.$t('expense-refund.charge-count'), value: 'count', width: 200},
        {text: this.$t('net'), value: 'amount', width: 200},
        {text: this.$t('energy'), value: 'energy', width: 200},
      ]
    },

    hasMembersWithoutCharges() {
      return this.members.some(item => item.energy === 0)
    },

    memberFiltered() {
      let members = this.members.filter(item => {
        if(this.search) {
          let search = this.search.toLowerCase()
          if(!item.customer.firstname.toLowerCase().includes(search) && !item.customer.lastname.toLowerCase().includes(search)) {
            return false
          }
        }
        return true
      })

      return members.filter(item => {
        if(!this.showMembersWithoutCharges) {
          if(item.amount === 0) {
            return false
          }
        }
        return true
      })
    },


    closureInfo() {
      const parts = []
      if (this.closedByUserName) parts.push(this.closedByUserName)
      if (this.closedAt) parts.push(this.formatUtcDate(this.closedAt, this.$t('format_datetime')))
      return parts.join(' — ')
    },

    organization() {
      return this.$parent.organization ?? null
    },

    periodId() {
      return this.$route.params.periodId
    },

    periodName() {
      if(this.startDate) {
        return this.formatUtcDate(this.startDate) + ' > ' + this.formatUtcDate(this.endDate)
      }
      return null
    },

  },

  methods: {


    loadData() {
      return new Promise((resolve, reject) => {
        this.loading = true

        let data = {
          searchText: this.search,
        }


        ExpenseRefundRepository.period(this.periodId, data).then(response => {
          this.startDate = response.startDate
          this.endDate = response.endDate
          this.status = response.status
          this.members = response.members
          this.loading = false

          if(this.members.length === 0) {
            if(!this.autoSynced) {
              this.autoSynced = true;
              // this.syncPeriod()
            }
          }

          resolve(response)

        }).catch(err => {
          this.notifyError(err)
          this.loading = false
          reject(err)
        })
      })
    },

    exportPdf() {
      this.showLoading(true)
      ExpenseRefundRepository.periodOrganizationPdf(this.periodId, this.organization.id).then(() => {
        this.showLoading(false)
      }).catch(e => {
        this.notifyError(e)
        this.showLoading(false)
      })
    },

    syncPeriod() {
      if(['PENDING', 'IN_PROGRESS'].includes(this.status)) {
        this.showLoading(true)
        ExpenseRefundRepository.syncPeriod(this.periodId).then(() => {
          this.showLoading(false)
          this.loadData().then(() => {
            this.$dialog.notify.success(this.$t('sync-success'))
          })
        }).catch(e => {
          this.notifyError(e)
          this.showLoading(false)
        })
      }
    },

    exportGlobal() {
      let month = dayjs(this.endDate).format('YYYY-MM');
      this.showLoading(true)
      ExpenseRefundRepository.globalSheet(month).then(() => {
        this.showLoading(false)
      }).catch(e => {
        this.notifyError(e)
        this.showLoading(false)
      })
    },

    exportGlobalQonto() {
      let month = dayjs(this.endDate).format('YYYY-MM');
      this.showLoading(true)
      ExpenseRefundRepository.globalQontoSheet(month).then(() => {
        this.showLoading(false)
      }).catch(e => {
        this.notifyError(e)
        this.showLoading(false)
      })
    },

    exportSheet() {
      this.showLoading(true)
      ExpenseRefundRepository.periodOrganizationSheet(this.periodId, this.organization.id).then(() => {
        this.showLoading(false)
      }).catch(e => {
        this.notifyError(e)
        this.showLoading(false)
      })
    },

    openSendMailModal() {
      this.$refs.sendMailModal.open()
    },

    openClosureModal() {
      this.$refs.closureModal.open()
    },

    openClosureLog() {
      this.closureLogLoading = true
      ExpenseRefundRepository.closureProgress(this.periodId).then(data => {
        this.closureLogLoading = false
        if (data && data.status) {
          this.$refs.closureModal.openWithProgress(data)
        } else {
          this.notifyInfo('expense-refund.closure.no-log')
        }
      }).catch(() => {
        this.closureLogLoading = false
        this.notifyInfo('expense-refund.closure.no-log')
      })
    },

    onClosureDone() {
      this.loadData()
    },

    checkClosureProgress() {
      // La page a pu être quittée pendant le chargement : plus de période dans l'adresse, rien à demander.
      if (!this.periodId) return
      ExpenseRefundRepository.closureProgress(this.periodId).then(data => {
        // Store closure info
        if (data.closedByUserName) {
          this.closedByUserName = data.closedByUserName
        }
        if (data.createdAt) {
          this.closedAt = data.createdAt
        }

        if (data.status === 'IN_PROGRESS' || data.status === 'FAILED') {
          this.$refs.closureModal.openWithProgress(data)
        }
      }).catch(() => {
        // No closure in progress
      })
    },

  },
}
</script>

<style lang="scss">
.member-search {
  min-height: 1000px;
  .member-list {
    td, th {
      cursor: pointer;
      &.text-start {
        text-align: left !important;
      }
    }
  }
}
</style>
